"use client"

import { Banknote, CreditCard, Store, CheckCircle2, Tag } from "lucide-react"
import Link from "next/link"
import { useEffect, useMemo, useState } from "react"

import { ApiService } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { GET_GENERAL_SETTINGS_POLICIES } from "@/lib/graphql"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { PaymentMethod } from "@/store/appointment-store"
import { Skeleton } from "@/app/components/ui/skeleton"
import { cn } from "@/lib/utils"
import {
  formatPaymentMethodHint,
  getEnabledBookingMethods,
  mergePaymentMethodsConfig,
  refreshAppointmentPaymentMethods,
} from "@/lib/utils/payment-methods"
import { observer } from "mobx-react-lite"
import { useQuery } from "@apollo/client/react"
import { useStore } from "@/app/components/common/store.provider"
import { GroupAvailabilityPanel } from "./GroupAvailabilityPanel"
import { toastUtils } from "@/utils/toast-utils"

const METHOD_ICONS: Record<PaymentMethod, typeof Store> = {
  Cash: Store,
  Transfer: Banknote,
  Card: CreditCard,
}

export const Step3 = observer(function Step3() {
  const { appointmentStore } = useStore()
  const isGroup = appointmentStore.isGroupMode
  const selectedPaymentMethod = appointmentStore.activePaymentMethod
  const isCustomBooking = appointmentStore.activeIsCustomBooking
  const [discountCode, setDiscountCode] = useState(appointmentStore.discountCode || "")
  const [validatingDiscount, setValidatingDiscount] = useState(false)
  const [discountError, setDiscountError] = useState<string | null>(null)
  const [discountSuccess, setDiscountSuccess] = useState<string | null>(null)
  const [policiesExpanded, setPoliciesExpanded] = useState(true)
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(
    () => appointmentStore.paymentMethodsConfig == null,
  )
  const bookingTotal = isGroup
    ? appointmentStore.groupTotalPrice
    : appointmentStore.selectedServices.reduce((sum, service) => sum + service.price, 0)
  const [policies, setPolicies] = useState<{
    cancellationPolicy?: string
    importantInfo?: string
  }>({})

  const paymentMethods = useMemo(
    () =>
      mergePaymentMethodsConfig(appointmentStore.paymentMethodsConfig ?? undefined),
    [appointmentStore.paymentMethodsConfig],
  )

  const enabledMethods = useMemo(
    () => getEnabledBookingMethods(paymentMethods),
    [paymentMethods],
  )

  const { data: graphqlData, error: graphqlError } = useQuery<{
    generalSettings?: { cancellationPolicy?: string; importantInfo?: string }
  }>(GET_GENERAL_SETTINGS_POLICIES, {
    errorPolicy: "all",
    fetchPolicy: "cache-first",
  })

  useEffect(() => {
    let mounted = true
    if (appointmentStore.paymentMethodsConfig) {
      setLoadingPaymentMethods(false)
    }
    void refreshAppointmentPaymentMethods(appointmentStore)
      .catch(() => {})
      .finally(() => {
        if (mounted) setLoadingPaymentMethods(false)
      })
    return () => {
      mounted = false
    }
  }, [appointmentStore])

  useEffect(() => {
    if (graphqlData?.generalSettings) {
      setPolicies({
        cancellationPolicy: graphqlData.generalSettings.cancellationPolicy,
        importantInfo: graphqlData.generalSettings.importantInfo,
      })
    } else if (graphqlError) {
      ApiService.getGeneralSettings()
        .then((res) => {
          setPolicies({
            cancellationPolicy: res.cancellationPolicy,
            importantInfo: res.importantInfo,
          })
        })
        .catch(() => {})
    }
  }, [graphqlData, graphqlError])

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return
    setValidatingDiscount(true)
    setDiscountError(null)
    setDiscountSuccess(null)
    try {
      const serviceIds = isGroup
        ? appointmentStore.groupMembers.flatMap((m) => m.services.map((s) => s.id))
        : appointmentStore.selectedServices.map((s) => s.id)

      const result = await ApiService.validateDiscount({
        code: discountCode.trim().toUpperCase(),
        serviceIds,
      })

      if (result.valid) {
        appointmentStore.setDiscountCode(discountCode.trim().toUpperCase())
        setDiscountSuccess(result.message || "Discount applied successfully!")
        toastUtils.success("Discount Applied", result.message)
      } else {
        setDiscountError(result.message || "Invalid discount code.")
        toastUtils.error("Discount Error", result.message)
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to validate discount code."
      setDiscountError(msg)
      toastUtils.error("Discount Error", msg)
    } finally {
      setValidatingDiscount(false)
    }
  }

  const handleRemoveDiscount = () => {
    appointmentStore.setDiscountCode("")
    setDiscountCode("")
    setDiscountSuccess(null)
    setDiscountError(null)
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Review and confirm</h1>

      {isGroup ? <GroupAvailabilityPanel /> : null}

      {isCustomBooking ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
          <p className="font-medium text-amber-900 dark:text-amber-100">Custom booking review</p>
          <p className="mt-1 text-amber-800 dark:text-amber-100/80">
            {isGroup
              ? "The salon team will review this request and schedule everyone in your party. Final amount and times are confirmed after approval."
              : "This request will be reviewed by the salon team. Final amount and preferred date/time will be confirmed after approval."}
          </p>
        </div>
      ) : (
        <div>
          <h2 className="text-sm font-medium text-foreground mb-3">Payment method</h2>
          {loadingPaymentMethods ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : enabledMethods.length === 0 ? (
            <p className="text-sm text-destructive">
              No payment methods are currently available. Please contact the salon.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {enabledMethods.map(({ method, label, key, description }) => {
                const Icon = METHOD_ICONS[method]
                const hint =
                  formatPaymentMethodHint(paymentMethods, key, bookingTotal) ??
                  description
                return (
                  <button
                    key={method}
                    type="button"
                    onClick={() => appointmentStore.setActivePaymentMethod(method)}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                      selectedPaymentMethod === method
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50",
                    )}
                  >
                    <div
                      className={cn(
                        "rounded-full p-2 shrink-0",
                        selectedPaymentMethod === method
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted",
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium">{label}</span>
                      {hint ? (
                        <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
                      ) : null}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {!isCustomBooking ? (
        <div>
          <h2 className="text-sm font-medium text-foreground mb-2">Discount code</h2>
          {appointmentStore.discountCode ? (
            <div className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-300 font-mono tracking-wide">
                    {appointmentStore.discountCode}
                  </span>
                  {discountSuccess ? (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      {discountSuccess}
                    </p>
                  ) : null}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveDiscount}
                className="h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                Remove
              </Button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter promo code (e.g. PROMO10)"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase())
                    setDiscountError(null)
                  }}
                  className="flex-1 min-h-11 font-mono uppercase"
                  disabled={validatingDiscount}
                />
                <Button
                  variant="outline"
                  type="button"
                  className="min-h-11"
                  onClick={handleApplyDiscount}
                  disabled={validatingDiscount || !discountCode.trim()}
                >
                  {validatingDiscount ? "Applying..." : "Apply"}
                </Button>
              </div>
              {discountError ? (
                <p className="text-xs text-destructive">{discountError}</p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      <div>
        <h2 className="mb-2 text-sm font-medium text-foreground">Notes</h2>
        <Textarea
          placeholder="Anything we should know? (optional)"
          value={appointmentStore.activeNotes}
          onChange={(event) => appointmentStore.setActiveNotes(event.target.value)}
          className="min-h-24"
          maxLength={500}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {isGroup
            ? "Applies to the whole party. Per-guest notes stay on the guests step."
            : "Share preferences, allergies, or other details for your appointment."}
        </p>
      </div>

      <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
        <h2 className="text-sm font-medium text-foreground">Before you confirm</h2>
        <label className="flex min-h-11 items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0"
            checked={appointmentStore.acceptedTerms}
            onChange={(event) =>
              appointmentStore.setAcceptedTerms(event.target.checked)
            }
          />
          <span>
            I agree to the{" "}
            <Link
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Terms
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>
        <label className="flex min-h-11 items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0"
            checked={appointmentStore.notificationsConsent}
            onChange={(event) =>
              appointmentStore.setNotificationsConsent(event.target.checked)
            }
          />
          <span>I agree to receive appointment updates and notifications.</span>
        </label>
      </div>

      <div>
        <button
          type="button"
          className="flex min-h-11 w-full items-center justify-between text-left text-sm font-medium text-foreground"
          onClick={() => setPoliciesExpanded((open) => !open)}
        >
          Cancellation policy & important info
          <span className="text-muted-foreground">{policiesExpanded ? "Hide" : "Show"}</span>
        </button>
        {policiesExpanded ? (
          <div className="mt-3 space-y-4">
            <div>
              <h2 className="mb-2 text-sm font-medium text-foreground">Cancellation policy</h2>
              {!policies.cancellationPolicy && !graphqlData && !graphqlError ? (
                <Skeleton className="h-12 w-full rounded-md" />
              ) : (
                <div
                  className="text-sm text-muted-foreground [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4 [&_p]:mb-2 last:[&_p]:mb-0 [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{
                    __html: policies.cancellationPolicy ?? "Please cancel at least 48 hours before appointment.",
                  }}
                />
              )}
            </div>
            <div>
              <h2 className="mb-2 text-sm font-medium text-foreground">Important info</h2>
              {!policies.importantInfo && !graphqlData && !graphqlError ? (
                <Skeleton className="h-24 w-full rounded-md" />
              ) : (
                <div
                  className="text-sm text-muted-foreground [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4 [&_p]:mb-2 last:[&_p]:mb-0 [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{
                    __html:
                      policies.importantInfo ??
                      "Lateness will not be tolerated. Please arrive on time for your appointment.",
                  }}
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
})
